import {ChangeEvent, useEffect, useMemo, useState} from 'react'
import {useMutation, useQuery} from '@apollo/client'
import {Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table} from 'react-bootstrap'
import Select from 'react-select'
import {useNavigate, useParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {useUploadFile} from '../../hooks/useUploadFile'
import {prependBackendUrl} from '../../gql/client'
import {DateInput} from '../../components/DateInput'
import {GET_ASSIGNMENT_USERS_LIST} from '../../gql/queries/usersQuery'
import {GET_MY_ASSIGNMENT_GROUPS, GET_TICKET_ASSIGNMENT_GROUPS} from '../../gql/queries/asiignmentGruopQuery'
import {
  ADD_TICKET_NOTE,
  CREATE_TICKET,
  CREATE_TICKET_ATTACHMENT_STR,
  DELETE_TICKET_ATTACHMENT,
  UPDATE_TICKET,
} from '../../gql/mutations/ticketsMutations'
import {GET_TICKET_BY_ID, GET_TICKET_TYPES} from '../../gql/queries/ticketsQuery'
import {hasPermission, isAdministrator, parseId} from '../../helpers'
import {ALL_PERMISSIONS} from '../../helpers/consts'
import {useGetUser} from '../../store/userStore'
import {IResTicketById, IResTicketTypes} from '../../types/tickets'
import {RenderFilePreview} from '../onePolicy/components/documents/RenderFilePreview'
import './AttentionTicketsPage.css'

type FormState = {
  title: string
  observations: string
  channel: 'INTERNAL' | 'PHONE' | 'WHATSAPP' | 'SMS' | 'EMAIL' | 'WEB' | 'API'
  externalReference: string
  status: string
  priority: string
  dueAt: string
  ticketTypeId: string
  assignmentType: 'USER' | 'GROUP'
  assignedUserId: string
  assignedGroupId: string
}

type TicketFormSection = 'details' | 'notes' | 'attachments' | 'history' | 'audit'
type UserSelectOption = {
  value: string
  label: string
  searchName: string
  searchEmail: string
}

const normalizeNodeId = (value?: string | null) => (value ? String(parseId(value)) : '')

const INITIAL_FORM: FormState = {
  title: '',
  observations: '',
  channel: 'INTERNAL',
  externalReference: '',
  status: 'OPEN',
  priority: 'MEDIUM',
  dueAt: '',
  ticketTypeId: '',
  assignmentType: 'USER',
  assignedUserId: '',
  assignedGroupId: '',
}

const formatDate = (value?: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString()
}

const toDueAtDateTime = (value: string) => {
  const raw = (value || '').trim()
  if (!raw) return null
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return `${raw}T00:00:00`
  }
  const parsed = new Date(raw)
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString()
}

const userLabel = (user?: {firstName?: string; lastName?: string; email?: string} | null) => {
  if (!user) return '-'
  const full = `${user.firstName || ''} ${user.lastName || ''}`.trim()
  return full || user.email || '-'
}

const STATUS_LABELS: Record<string, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In Progress',
  WAITING_CUSTOMER: 'Waiting Customer',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
}

const formatStatus = (status?: string | null) => {
  if (!status) return 'N/A'
  return STATUS_LABELS[status] || status
}

const prettifyFieldName = (field?: string | null) => {
  if (!field) return 'Unknown field'
  return field
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

const formatAuditValue = (value?: string | null) => {
  if (!value) return 'Empty'
  const trimmed = value.trim()
  if (!trimmed) return 'Empty'
  if (trimmed.length > 140) return `${trimmed.slice(0, 140)}...`
  return trimmed
}

export const TicketFormPage = () => {
  const navigate = useNavigate()
  const {id = 'new'} = useParams()
  const isCreate = id === 'new'
  const permissions = useGetUser((s) => s.user?.permissions) ?? []
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const currentUserId = useGetUser((s) => s.user?.user?.id) || ''
  const isAdminUser = isAdministrator(permissions)
  const isSupervisorUser = userGroups.includes('Supervisor')
  // Regla de producto: cualquier usuario autenticado puede crear tickets.
  // Los permisos específicos aplican para edición/acciones posteriores.
  const canCreateTicket = true
  const canChangeTicket = isAdminUser || hasPermission(permissions, ALL_PERMISSIONS.CHANGE_TICKET)
  const canAssignTicket = isAdminUser || hasPermission(permissions, ALL_PERMISSIONS.ASSIGN_TICKET)
  const canChangeTicketStatus = isAdminUser || hasPermission(permissions, ALL_PERMISSIONS.CHANGE_TICKET_STATUS)
  const canManageTicketSla = isAdminUser || hasPermission(permissions, ALL_PERMISSIONS.MANAGE_TICKET_SLA)
  const canCommentTicket = isAdminUser || hasPermission(permissions, ALL_PERMISSIONS.COMMENT_TICKET)
  const canDeleteTicket = isAdminUser || hasPermission(permissions, ALL_PERMISSIONS.DELETE_TICKET)
  const canViewAuditLog = isAdminUser
  const canDownloadAttachment = hasPermission(permissions, ALL_PERMISSIONS.DOWNLOAD_TICKET_ATTACHMENT)

  const [form, setForm] = useState<FormState>(INITIAL_FORM)
  const [noteText, setNoteText] = useState('')
  const [activeSection, setActiveSection] = useState<TicketFormSection>('details')

  const {data, loading, error, refetch} = useQuery<IResTicketById>(GET_TICKET_BY_ID, {
    variables: {id: isCreate ? '0' : parseId(id)},
    skip: isCreate,
    fetchPolicy: 'no-cache',
  })
  const {data: dataTypes} = useQuery<IResTicketTypes>(GET_TICKET_TYPES, {
    variables: {isActive: true},
    fetchPolicy: 'cache-first',
  })
  const {data: dataUsers} = useQuery(GET_ASSIGNMENT_USERS_LIST, {
    variables: {assignmentCandidates: true},
    fetchPolicy: 'cache-first',
  })
  const {data: dataGroups} = useQuery(GET_TICKET_ASSIGNMENT_GROUPS, {fetchPolicy: 'cache-first'})
  const {data: dataMyGroups} = useQuery(GET_MY_ASSIGNMENT_GROUPS, {fetchPolicy: 'cache-first'})

  const [createTicket, {loading: creating}] = useMutation(CREATE_TICKET)
  const [updateTicket, {loading: updating}] = useMutation(UPDATE_TICKET)
  const [addNote, {loading: savingNote}] = useMutation(ADD_TICKET_NOTE)
  const [deleteAttachment] = useMutation(DELETE_TICKET_ATTACHMENT)
  const [uploadFile, {loading: uploading}] = useUploadFile<any>()

  const ticket = data?.ticketById
  const ticketId = ticket?.id ? parseId(ticket.id) : isCreate ? '' : parseId(id)
  const normalizedCurrentUserId = normalizeNodeId(currentUserId)
  const myGroupIds = useMemo(
    () => (dataMyGroups?.myAssignmentGroups || []).map((group: any) => normalizeNodeId(group?.id)),
    [dataMyGroups]
  )
  const isAssignedUser =
    !isCreate &&
    Boolean(ticket?.assignedUser?.id) &&
    normalizeNodeId(ticket?.assignedUser?.id) === normalizedCurrentUserId
  const isAssignedGroupMember =
    !isCreate &&
    Boolean(ticket?.assignedGroup?.id) &&
    myGroupIds.includes(normalizeNodeId(ticket?.assignedGroup?.id))
  const isTicketCreator =
    !isCreate &&
    Boolean(ticket?.createdBy?.id) &&
    normalizeNodeId(ticket?.createdBy?.id) === normalizedCurrentUserId
  const canManageByContext = Boolean(isTicketCreator || isAssignedUser || (isSupervisorUser && !isCreate))
  const canAssignByGroupMemberContext = Boolean(isAssignedGroupMember)
  const canEditSubjectEffective = isCreate ? canCreateTicket : canChangeTicket || isTicketCreator || isAssignedUser || isSupervisorUser
  const canChangeTicketEffective = canChangeTicket || canManageByContext
  const canAssignTicketEffective = canAssignTicket || canManageByContext || canAssignByGroupMemberContext
  const canAssignToMeByContext = Boolean(isAssignedGroupMember && !isAssignedUser)
  const canChangeTicketStatusEffective = canChangeTicketStatus || canManageByContext
  const canManageTicketSlaEffective = canManageTicketSla || canManageByContext
  const canCommentTicketEffective = canCommentTicket || canManageByContext
  const canUploadAttachmentEffective = canChangeTicketEffective
  const canEdit = isCreate
    ? canCreateTicket
    : canChangeTicketEffective || canAssignTicketEffective || canChangeTicketStatusEffective || canManageTicketSlaEffective
  const canEditTicketFields = isCreate ? canCreateTicket : canChangeTicketEffective
  const canAssignTicketFields = isCreate ? canCreateTicket : canAssignTicketEffective
  const canAssignOnlyWithinCurrentGroup = Boolean(!canAssignTicket && isAssignedGroupMember && !canManageByContext)
  const canManageSlaFields = isCreate ? canCreateTicket : canManageTicketSlaEffective
  const showAssignToMeButton =
    !isCreate &&
    Boolean(ticket?.assignedGroup?.id) &&
    canAssignToMeByContext

  useEffect(() => {
    if (!ticket) return
    setForm({
      title: ticket.title || '',
      observations: ticket.observations || '',
      channel: ticket.channel || 'INTERNAL',
      externalReference: ticket.externalReference || '',
      status: ticket.status || 'OPEN',
      priority: ticket.priority || 'MEDIUM',
      dueAt: ticket.dueAt ? String(ticket.dueAt).slice(0, 10) : '',
      ticketTypeId: normalizeNodeId(ticket.ticketType?.id || ''),
      assignmentType: ticket.assignmentType || 'USER',
      assignedUserId: normalizeNodeId(ticket.assignedUser?.id || ''),
      assignedGroupId: normalizeNodeId(ticket.assignedGroup?.id || ''),
    })
  }, [ticket])

  useEffect(() => {
    if (error) toast.error(`Error loading ticket: ${error.message}`)
  }, [error])

  useEffect(() => {
    if (isCreate && activeSection !== 'details') setActiveSection('details')
  }, [activeSection, isCreate])

  useEffect(() => {
    if (!canViewAuditLog && activeSection === 'audit') setActiveSection('history')
  }, [activeSection, canViewAuditLog])

  const users = useMemo(() => dataUsers?.allUsersList || [], [dataUsers])
  const groups = useMemo(() => dataGroups?.ticketAssignmentGroups || [], [dataGroups])
  const ticketTypes = useMemo(() => dataTypes?.ticketTypes || [], [dataTypes])
  const assignedUserOptions: UserSelectOption[] = useMemo(
    () =>
      users.map((user: any) => {
        const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || 'No name'
        const email = user.email || 'No email'
        return {
          value: normalizeNodeId(user.id),
          label: `${fullName} - ${email}`,
          searchName: fullName.toLowerCase(),
          searchEmail: String(email).toLowerCase(),
        }
      }),
    [users]
  )
  const selectedAssignedUserOption = useMemo(
    () => assignedUserOptions.find((option) => String(option.value) === String(form.assignedUserId)) || null,
    [assignedUserOptions, form.assignedUserId]
  )
  const busy = creating || updating || savingNote || uploading
  const statusHistory = ticket?.statusHistory || []
  const changeLogs = ticket?.changeLogs || []

  const statusHistorySummary = useMemo(() => {
    const transitions = statusHistory.length
    const withComments = statusHistory.filter((item) => Boolean(item.comment?.trim())).length
    const waitingCustomerCount = statusHistory.filter((item) => item.newStatus === 'WAITING_CUSTOMER').length
    const reopenCount = statusHistory.filter((item) =>
      ['RESOLVED', 'CLOSED'].includes(item.previousStatus || '') && ['OPEN', 'IN_PROGRESS', 'WAITING_CUSTOMER'].includes(item.newStatus)
    ).length
    const latest = statusHistory[0]
    return {
      transitions,
      withComments,
      waitingCustomerCount,
      reopenCount,
      latestAt: latest?.changedAt || null,
      latestBy: userLabel(latest?.changedBy),
    }
  }, [statusHistory])

  const auditSummary = useMemo(() => {
    const totalChanges = changeLogs.length
    const withComments = changeLogs.filter((item) => Boolean(item.comment?.trim())).length
    const distinctFields = new Set(changeLogs.map((item) => item.fieldName).filter(Boolean)).size
    const distinctActors = new Set(changeLogs.map((item) => item.changedBy?.id || item.changedBy?.email).filter(Boolean)).size
    const latest = changeLogs[0]
    return {
      totalChanges,
      withComments,
      distinctFields,
      distinctActors,
      latestAt: latest?.changedAt || null,
      latestBy: userLabel(latest?.changedBy),
    }
  }, [changeLogs])

  const sectionItems: Array<{
    key: TicketFormSection
    title: string
    subtitle: string
    icon: string
    disabled?: boolean
    count?: number
  }> = [
    {
      key: 'details',
      title: 'Ticket Data',
      subtitle: 'General and assignment',
      icon: 'bi-card-checklist',
    },
    {
      key: 'notes',
      title: 'Notes',
      subtitle: 'Internal comments',
      icon: 'bi-journal-text',
      disabled: isCreate,
      count: ticket?.notes?.length || 0,
    },
    {
      key: 'attachments',
      title: 'Attachments',
      subtitle: 'Files and evidence',
      icon: 'bi-paperclip',
      disabled: isCreate,
      count: ticket?.attachments?.length || 0,
    },
    {
      key: 'history',
      title: 'Status History',
      subtitle: 'Status transitions',
      icon: 'bi-clock-history',
      disabled: isCreate,
      count: statusHistory.length,
    },
    ...(canViewAuditLog
      ? [
          {
            key: 'audit' as TicketFormSection,
            title: 'Audit Log',
            subtitle: 'Changed fields',
            icon: 'bi-list-columns-reverse',
            disabled: isCreate,
            count: changeLogs.length,
          },
        ]
      : []),
  ]

  const onSave = async () => {
    if (isCreate && !canCreateTicket) {
      toast.error('No permission to create tickets')
      return
    }
    if (!isCreate && !canEdit) {
      toast.error('No permission to update this ticket')
      return
    }

    try {
      if (isCreate) {
        if (!form.title.trim()) {
          toast.warning('Subject is required')
          return
        }
        if (form.assignmentType === 'USER' && !form.assignedUserId) {
          toast.warning('Assigned user is required')
          return
        }
        if (form.assignmentType === 'GROUP' && !form.assignedGroupId) {
          toast.warning('Assigned group is required')
          return
        }
        const createPayload = {
          title: form.title.trim(),
          observations: form.observations.trim(),
          channel: form.channel,
          externalReference: form.externalReference.trim(),
          status: 'OPEN',
          priority: form.priority,
          dueAt: toDueAtDateTime(form.dueAt),
          ticketTypeId: form.ticketTypeId ? parseId(form.ticketTypeId) : null,
          assignmentType: form.assignmentType,
          assignedUserId: form.assignedUserId ? parseId(form.assignedUserId) : null,
          assignedGroupId: form.assignmentType === 'GROUP' && form.assignedGroupId ? parseId(form.assignedGroupId) : null,
        }
        const res: any = await createTicket({variables: {ticketData: createPayload}})
        const ok = res?.data?.createTicket?.success
        if (!ok) throw new Error(res?.data?.createTicket?.errors?.[0]?.message || 'Could not create ticket')
        const createdId = res?.data?.createTicket?.ticket?.id
        toast.success('Ticket created')
        navigate(`/tickets/${parseId(createdId)}`)
        return
      }

      const ticketData: Record<string, any> = {
        expectedUpdatedAt: ticket?.updatedAt || null,
      }

      if (canEditSubjectEffective) {
        if (!form.title.trim()) {
          toast.warning('Subject is required')
          return
        }
        ticketData.title = form.title.trim()
      }

      if (canChangeTicketEffective) {
        ticketData.observations = form.observations.trim()
        ticketData.channel = form.channel
        ticketData.externalReference = form.externalReference.trim()
        ticketData.priority = form.priority
        ticketData.ticketTypeId = form.ticketTypeId ? parseId(form.ticketTypeId) : null
      }

      if (canAssignTicketEffective) {
        ticketData.assignmentType = form.assignmentType
        ticketData.assignedUserId = form.assignedUserId ? parseId(form.assignedUserId) : null
        ticketData.assignedGroupId =
          form.assignmentType === 'GROUP' && form.assignedGroupId ? parseId(form.assignedGroupId) : null
      }

      if (canChangeTicketStatusEffective) {
        ticketData.status = form.status
      }

      if (canManageTicketSlaEffective) {
        ticketData.dueAt = toDueAtDateTime(form.dueAt)
      }

      const res: any = await updateTicket({
        variables: {
          id: parseId(id),
          ticketData,
        },
      })
      const ok = res?.data?.updateTicket?.success
      if (!ok) throw new Error(res?.data?.updateTicket?.errors?.[0]?.message || 'Could not update ticket')
      toast.success('Ticket updated')
      refetch?.()
    } catch (err: any) {
      toast.error(`Error: ${err.message}`)
    }
  }

  const onAddNote = async () => {
    if (!canCommentTicketEffective) {
      toast.error('No permission to comment on tickets')
      return
    }
    if (!noteText.trim() || !ticketId) return
    try {
      const res: any = await addNote({
        variables: {
          ticketId: parseId(ticketId),
          comment: noteText.trim(),
        },
      })
      if (!res?.data?.addTicketNote?.success) {
        throw new Error(res?.data?.addTicketNote?.errors?.[0]?.message || 'Could not save note')
      }
      setNoteText('')
      toast.success('Note saved')
      refetch?.()
    } catch (err: any) {
      toast.error(`Error: ${err.message}`)
    }
  }

  const onUploadFiles = (files: FileList | null) => {
    if (!canUploadAttachmentEffective) {
      toast.error('No permission to upload attachments')
      return
    }
    if (!files || !ticketId) return
    Array.from(files).forEach((file) => {
      const formdata = new FormData()
      formdata.append('0', file, file.name)
      formdata.append('map', '{"0": ["variables.file"]}')
      formdata.append(
        'operations',
        JSON.stringify({
          query: CREATE_TICKET_ATTACHMENT_STR,
          variables: {
            ticketId: parseId(ticketId),
            file: null,
            originalName: file.name,
          },
        })
      )
      uploadFile(formdata)
    })
    toast.info('Uploading files...')
    setTimeout(() => refetch?.(), 1200)
  }

  const onDeleteAttachment = async (attachmentId: string) => {
    try {
      const res: any = await deleteAttachment({variables: {id: parseId(attachmentId)}})
      if (!res?.data?.deleteTicketAttachment?.success) {
        throw new Error(res?.data?.deleteTicketAttachment?.errors?.[0]?.message || 'Could not delete attachment')
      }
      toast.success('Attachment deleted')
      refetch?.()
    } catch (err: any) {
      toast.error(`Error: ${err.message}`)
    }
  }

  const onAssignToMe = async () => {
    if (!ticket || !ticket.id || !ticket.assignedGroup?.id || !currentUserId) return
    try {
      const res: any = await updateTicket({
        variables: {
          id: parseId(ticket.id),
          ticketData: {
            expectedUpdatedAt: ticket.updatedAt || null,
            assignmentType: 'GROUP',
            assignedGroupId: parseId(ticket.assignedGroup.id),
            assignedUserId: parseId(currentUserId),
          },
        },
      })
      const ok = res?.data?.updateTicket?.success
      if (!ok) throw new Error(res?.data?.updateTicket?.errors?.[0]?.message || 'Could not assign ticket to current user')
      toast.success('Ticket assigned to you')
      refetch?.()
    } catch (err: any) {
      toast.error(`Error: ${err.message}`)
    }
  }

  const sectionUnavailable = (message: string) => (
    <Alert variant='info' className='mb-0'>
      {message}
    </Alert>
  )

  const renderSectionContent = () => {
    if (activeSection === 'details') {
      return (
        <>
          <div className='ticket-form-panel__title'>
            <h5 className='mb-1'>Ticket Data</h5>
            <p className='mb-0 text-muted'>Complete the general ticket information and assignment.</p>
          </div>
          {!isCreate && (
            <div className='d-flex align-items-center justify-content-between mt-3 mb-2'>
              <div className='d-flex flex-wrap align-items-center gap-2'>
                <Badge bg='secondary'>
                  Group: {ticket?.assignedGroup?.name || 'Unassigned'}
                </Badge>
                <Badge bg='primary'>
                  <span className='text-white'>
                    User: {userLabel(ticket?.assignedUser) === '-' ? 'Unassigned' : userLabel(ticket?.assignedUser)}
                  </span>
                </Badge>
              </div>
              {showAssignToMeButton && (
                <Button size='sm' onClick={onAssignToMe} disabled={busy}>
                  Assign to me
                </Button>
              )}
            </div>
          )}
          <Row className='g-3 ticket-form-grid mt-1'>
            <Col xl={6} lg={12}>
              <Form.Label>Subject *</Form.Label>
              <Form.Control
                value={form.title}
                onChange={(e) => setForm((prev) => ({...prev, title: e.target.value}))}
                disabled={!canEditSubjectEffective}
              />
            </Col>
            <Col xl={2} lg={4} md={6}>
              <Form.Label>Channel</Form.Label>
              <Form.Select value={form.channel} onChange={(e) => setForm((prev) => ({...prev, channel: e.target.value as FormState['channel']}))} disabled={!canEditTicketFields}>
                <option value='INTERNAL'>Internal</option>
                <option value='PHONE'>Phone</option>
                <option value='WHATSAPP'>WhatsApp</option>
                <option value='SMS'>SMS</option>
                <option value='EMAIL'>Email</option>
                <option value='WEB'>Web</option>
                <option value='API'>API</option>
              </Form.Select>
            </Col>
            <Col xl={2} lg={4} md={6}>
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={form.status}
                onChange={(e) => setForm((prev) => ({...prev, status: e.target.value}))}
                disabled={isCreate || !canChangeTicketStatusEffective}
              >
                <option value='OPEN'>Open</option>
                <option value='IN_PROGRESS'>In Progress</option>
                <option value='WAITING_CUSTOMER'>Waiting Customer</option>
                <option value='RESOLVED'>Resolved</option>
                <option value='CLOSED'>Closed</option>
              </Form.Select>
            </Col>
            <Col xl={2} lg={4} md={6}>
              <Form.Label>Priority</Form.Label>
              <Form.Select value={form.priority} onChange={(e) => setForm((prev) => ({...prev, priority: e.target.value}))} disabled={!canEditTicketFields}>
                <option value='LOW'>Low</option>
                <option value='MEDIUM'>Medium</option>
                <option value='HIGH'>High</option>
                <option value='URGENT'>Urgent</option>
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>Type</Form.Label>
              <Form.Select value={form.ticketTypeId} onChange={(e) => setForm((prev) => ({...prev, ticketTypeId: e.target.value}))} disabled={!canEditTicketFields}>
                <option value=''>Select type</option>
                {ticketTypes.map((ticketType) => <option key={ticketType.id} value={ticketType.id}>{ticketType.name}</option>)}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label>Assignment</Form.Label>
              <Form.Select
                value={form.assignmentType}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    assignmentType: e.target.value as 'USER' | 'GROUP',
                    assignedGroupId: e.target.value === 'USER' ? '' : prev.assignedGroupId,
                  }))
                }
                disabled={!canAssignTicketFields || canAssignOnlyWithinCurrentGroup}
              >
                <option value='USER'>User</option>
                <option value='GROUP'>Group</option>
              </Form.Select>
            </Col>
            <Col md={6}>
              {form.assignmentType === 'USER' ? (
                <>
                  <Form.Label>Assigned User</Form.Label>
                  <Select
                    value={selectedAssignedUserOption}
                    options={assignedUserOptions}
                    onChange={(selectedOption) =>
                      setForm((prev) => ({
                        ...prev,
                        assignedUserId: (selectedOption as UserSelectOption | null)?.value || '',
                      }))
                    }
                    placeholder={isCreate ? 'Select user' : 'Unassigned'}
                    isDisabled={!canAssignTicketFields}
                    isClearable={!isCreate}
                    noOptionsMessage={({inputValue}) =>
                      inputValue ? 'No users found' : 'No users available'
                    }
                    filterOption={(option, rawInput) => {
                      const input = rawInput.toLowerCase().trim()
                      if (!input) return true
                      const data = option.data as UserSelectOption
                      return (
                        data.searchName.includes(input) ||
                        data.searchEmail.includes(input) ||
                        data.label.toLowerCase().includes(input)
                      )
                    }}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({...base, zIndex: 9999}),
                    }}
                  />
                  {canAssignOnlyWithinCurrentGroup && (
                    <small className='text-muted d-block mt-1'>
                      Reassign to another user from this selector. Only users from the same assigned group are allowed.
                    </small>
                  )}
                </>
              ) : (
                <>
                  <Form.Label>Assigned Group</Form.Label>
                  <Form.Select
                    value={form.assignedGroupId}
                    onChange={(e) => setForm((prev) => ({...prev, assignedGroupId: e.target.value, assignedUserId: ''}))}
                    disabled={!canAssignTicketFields || canAssignOnlyWithinCurrentGroup}
                  >
                    <option value=''>{isCreate ? 'Select group' : 'Unassigned'}</option>
                    {groups.map((group: any) => (
                      <option key={group.id} value={normalizeNodeId(group.id)}>
                        {group.name}
                      </option>
                    ))}
                  </Form.Select>
                </>
              )}
            </Col>
            <Col md={12}>
              <Row className='g-2'>
                <Col md={6}>
                  <Form.Label>External Reference</Form.Label>
                  <Form.Control
                    value={form.externalReference}
                    onChange={(e) => setForm((prev) => ({...prev, externalReference: e.target.value}))}
                    placeholder='Optional external ID from channel/provider'
                    disabled={!canEditTicketFields}
                  />
                </Col>
                <Col md={6}>
                  <DateInput
                    id='dueAt'
                    name='dueAt'
                    label='Due Date (SLA)'
                    value={form.dueAt}
                    onChange={(_, value) =>
                      setForm((prev) => ({
                        ...prev,
                        dueAt: value || '',
                      }))
                    }
                    disabled={!canManageSlaFields}
                  />
                </Col>
              </Row>
            </Col>
            <Col md={12}>
              <Form.Label>Observations</Form.Label>
              <Form.Control as='textarea' rows={5} value={form.observations} onChange={(e) => setForm((prev) => ({...prev, observations: e.target.value}))} disabled={!canEditTicketFields} />
            </Col>
          </Row>
        </>
      )
    }

    if (activeSection === 'attachments') {
      if (isCreate) return sectionUnavailable('Create the ticket first to upload attachments.')
      return (
        <>
          <div className='ticket-form-panel__title'>
            <h5 className='mb-1'>Attachments</h5>
            <p className='mb-0 text-muted'>Upload supporting files and manage current attachments.</p>
          </div>
          <Form.Control
            type='file'
            multiple
            onChange={(e: ChangeEvent<HTMLInputElement>) => onUploadFiles(e.target.files)}
            disabled={!canUploadAttachmentEffective}
            className='mt-3'
          />
          <Table hover className='mt-3'>
            <thead>
              <tr><th></th><th>File</th><th>By</th><th>Created</th><th></th></tr>
            </thead>
            <tbody>
              {(ticket?.attachments || []).map((attachment) => (
                <tr key={attachment.id}>
                  <td><RenderFilePreview file={prependBackendUrl(attachment.file)} /></td>
                  <td>{attachment.originalName || attachment.file}</td>
                  <td>{userLabel(attachment.uploadedBy)}</td>
                  <td>{formatDate(attachment.createdAt)}</td>
                  <td className='text-end'>
                    {canDownloadAttachment && (
                      <>
                        <Button variant='outline-primary' size='sm' onClick={() => window.open(prependBackendUrl(attachment.file), '_blank')}>View</Button>{' '}
                      </>
                    )}
                    {(canUploadAttachmentEffective || canDeleteTicket) && (
                      <Button variant='outline-danger' size='sm' onClick={() => onDeleteAttachment(attachment.id)}>Delete</Button>
                    )}
                  </td>
                </tr>
              ))}
              {(ticket?.attachments || []).length === 0 && <tr><td colSpan={5} className='text-center text-muted'>No attachments yet.</td></tr>}
            </tbody>
          </Table>
        </>
      )
    }

    if (activeSection === 'notes') {
      if (isCreate) return sectionUnavailable('Create the ticket first to add notes.')
      return (
        <>
          <div className='ticket-form-panel__title'>
            <h5 className='mb-1'>Notes</h5>
            <p className='mb-0 text-muted'>Track internal communication related to this ticket.</p>
          </div>
          <div className='d-flex flex-column gap-2 mt-3 mb-3'>
            {(ticket?.notes || []).map((note) => (
              <div key={note.id} className='border rounded p-3 bg-light'>
                <div className='d-flex justify-content-between'>
                  <strong>{userLabel(note.author)}</strong>
                  <small>{formatDate(note.createdAt)}</small>
                </div>
                <div>{note.comment}</div>
              </div>
            ))}
            {(ticket?.notes || []).length === 0 && <span className='text-muted'>No notes yet.</span>}
          </div>
          <Form.Control
            as='textarea'
            rows={4}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder='Write a comment...'
            disabled={!canCommentTicketEffective}
          />
          <div className='mt-2'>
            <Button size='sm' onClick={onAddNote} disabled={!noteText.trim() || busy || !canCommentTicketEffective}>Add Note</Button>
          </div>
        </>
      )
    }

    if (activeSection === 'history') {
      if (isCreate) return sectionUnavailable('Create the ticket first to view status history.')
      return (
        <>
          <div className='ticket-form-panel__title'>
            <h5 className='mb-1'>Status History</h5>
            <p className='mb-0 text-muted'>Detailed timeline of transitions, owners, and context comments.</p>
          </div>
          <div className='ticket-form-metrics'>
            <div className='ticket-form-metric'>
              <small>Total transitions</small>
              <strong>{statusHistorySummary.transitions}</strong>
            </div>
            <div className='ticket-form-metric'>
              <small>With comments</small>
              <strong>{statusHistorySummary.withComments}</strong>
            </div>
            <div className='ticket-form-metric'>
              <small>Waiting customer</small>
              <strong>{statusHistorySummary.waitingCustomerCount}</strong>
            </div>
            <div className='ticket-form-metric'>
              <small>Reopen events</small>
              <strong>{statusHistorySummary.reopenCount}</strong>
            </div>
          </div>
          {statusHistorySummary.latestAt && (
            <div className='ticket-form-meta-note'>
              Latest transition by <strong>{statusHistorySummary.latestBy}</strong> on {formatDate(statusHistorySummary.latestAt)}
            </div>
          )}
          <div className='mt-3'>
            {statusHistory.map((item, index) => (
              <div key={item.id} className='ticket-form-history-row'>
                <div className='ticket-form-history-row__head'>
                  <span className='ticket-form-history-row__index'>#{statusHistory.length - index}</span>
                  <span className='ticket-form-history-row__transition'>
                    <Badge bg='secondary'>{formatStatus(item.previousStatus || 'INITIAL')}</Badge>
                    <i className='bi bi-arrow-right mx-2 text-muted' />
                    <Badge bg='primary'>{formatStatus(item.newStatus)}</Badge>
                  </span>
                </div>
                <div className='ticket-form-history-row__meta'>
                  <span><strong>Changed by:</strong> {userLabel(item.changedBy)}</span>
                  <span><strong>Date:</strong> {formatDate(item.changedAt)}</span>
                </div>
                <div className='ticket-form-history-row__comment'>
                  <strong>Comment:</strong> {item.comment?.trim() || 'No comment'}
                </div>
              </div>
            ))}
            {statusHistory.length === 0 && <span className='text-muted'>No status history yet.</span>}
          </div>
        </>
      )
    }

    if (!canViewAuditLog) return sectionUnavailable('Audit log is available only for administrators.')
    if (isCreate) return sectionUnavailable('Create the ticket first to view audit logs.')
    return (
      <>
        <div className='ticket-form-panel__title'>
          <h5 className='mb-1'>Audit Log</h5>
          <p className='mb-0 text-muted'>Detailed field-level change tracking with before and after values.</p>
        </div>
        <div className='ticket-form-metrics'>
          <div className='ticket-form-metric'>
            <small>Total changes</small>
            <strong>{auditSummary.totalChanges}</strong>
          </div>
          <div className='ticket-form-metric'>
            <small>Fields impacted</small>
            <strong>{auditSummary.distinctFields}</strong>
          </div>
          <div className='ticket-form-metric'>
            <small>Users involved</small>
            <strong>{auditSummary.distinctActors}</strong>
          </div>
          <div className='ticket-form-metric'>
            <small>With comments</small>
            <strong>{auditSummary.withComments}</strong>
          </div>
        </div>
        {auditSummary.latestAt && (
          <div className='ticket-form-meta-note'>
            Latest change by <strong>{auditSummary.latestBy}</strong> on {formatDate(auditSummary.latestAt)}
          </div>
        )}
        <div className='mt-3'>
          <Table responsive hover className='ticket-form-audit-table'>
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>User</th>
                <th>Field</th>
                <th>Previous value</th>
                <th>New value</th>
                <th>Comment</th>
              </tr>
            </thead>
            <tbody>
              {changeLogs.map((item, index) => (
                <tr key={item.id}>
                  <td>{changeLogs.length - index}</td>
                  <td>{formatDate(item.changedAt)}</td>
                  <td>{userLabel(item.changedBy)}</td>
                  <td>{prettifyFieldName(item.fieldName)}</td>
                  <td><code>{formatAuditValue(item.oldValue)}</code></td>
                  <td><code>{formatAuditValue(item.newValue)}</code></td>
                  <td>{item.comment?.trim() || 'No comment'}</td>
                </tr>
              ))}
              {changeLogs.length === 0 && (
                <tr>
                  <td colSpan={7} className='text-center text-muted'>No audit log yet.</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </>
    )
  }

  if (!isCreate && loading) {
    return (
      <Modal className='ticket-form-modal' show onHide={() => navigate('/tickets')} size='xl' backdrop='static' keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>{`Edit ticket #${id}`}</Modal.Title>
        </Modal.Header>
        <Modal.Body className='ticket-form-modal__body'>
          <div className='d-flex flex-center py-10'>
            <Spinner animation='border' />
          </div>
        </Modal.Body>
      </Modal>
    )
  }

  return (
    <Modal className='ticket-form-modal' show onHide={() => navigate('/tickets')} size='xl' backdrop='static' keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title>{isCreate ? 'Create a new ticket' : `Edit ticket #${ticket?.id || id}`}</Modal.Title>
      </Modal.Header>
      <Modal.Body className='ticket-form-modal__body'>
        <div className='attention-tickets-page ticket-form-modal__content'>
          <div className='ticket-form-modal__actions'>
            <Button size='sm' onClick={onSave} disabled={busy || !canEdit}>
              {isCreate ? 'Create Ticket' : 'Save Changes'}
            </Button>
          </div>

          <div className='ticket-form-layout'>
            <Card className='ticket-form-sidebar'>
              <Card.Body className='p-2 p-lg-3'>
                {sectionItems.map((section) => (
                  <button
                    type='button'
                    key={section.key}
                    className={`ticket-form-sidebar__item ${activeSection === section.key ? 'is-active' : ''}`}
                    onClick={() => !section.disabled && setActiveSection(section.key)}
                    disabled={Boolean(section.disabled)}
                  >
                    <span className='ticket-form-sidebar__icon'>
                      <i className={`bi ${section.icon}`} />
                    </span>
                    <span className='ticket-form-sidebar__text'>
                      <span className='ticket-form-sidebar__title'>{section.title}</span>
                      <span className='ticket-form-sidebar__subtitle'>{section.subtitle}</span>
                    </span>
                    {typeof section.count === 'number' && (
                      <Badge bg={section.count > 0 ? 'primary' : 'secondary'}>{section.count}</Badge>
                    )}
                  </button>
                ))}
              </Card.Body>
            </Card>

            <Card className='ticket-form-panel'>
              <Card.Body className='ticket-form-panel__body'>
                <div className='ticket-form-panel__context'>
                  <i className='bi bi-layout-sidebar-inset me-2' />
                  {isCreate ? 'NEW TICKET WORKSPACE' : `TICKET #${ticket?.id || id} WORKSPACE`}
                </div>
                {renderSectionContent()}
              </Card.Body>
            </Card>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}
